import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createBlock as _createBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["aria-disabled", "aria-label"]
const _hoisted_2 = {
  key: 0,
  class: "lcb-spin",
  "aria-hidden": "true"
}
const _hoisted_3 = {
  key: 1,
  class: "lcb-ico",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  "stroke-width": "2.6",
  "stroke-linecap": "round",
  "stroke-linejoin": "round",
  "aria-hidden": "true"
}
const _hoisted_4 = {
  key: 2,
  class: "lcb-ico",
  viewBox: "0 0 24 24",
  fill: "currentColor",
  "aria-hidden": "true"
}
const _hoisted_5 = {
  key: 3,
  class: "lcb-label"
}

import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import Swal from 'sweetalert2'
import { loadLinkus, useLinkus } from '@/composables/useLinkus'
import { resolveCallNumber, startLinkusCall } from '@/services/wootdashLinkus'
import { placeCall, useBrowserPhone } from '@/composables/useBrowserPhone'


export default /*@__PURE__*/_defineComponent({
  __name: 'LinkusCallButton',
  props: {
    conversationId: {},
    phone: {},
    contactName: { default: '' },
    variant: { default: 'light' }
  },
  setup(__props: any) {

// Botão "Ligar" (Linkus): o PABX toca o Linkus do agente e, quando ele atende, disca pro contato da conversa.
// Sem como ligar (Linkus desligado/não configurado, agente sem ramal, contato sem telefone) fica apagado com o
// motivo no tooltip — o tooltip é próprio (Teleport) porque botão desabilitado não mostra o title em todo navegador.
// Variantes: "dark" (cabeçalho vermelho do chat), "light" (modo foco) e "row" (só o ícone, na linha do grid).
const props = __props

const store = useStore()
const token = computed<string>(() => store.getters.authUser?.wootdash_token ?? '')
const { availability } = useLinkus()

const state = ref<'idle' | 'calling' | 'done'>('idle')
const { phone } = useBrowserPhone()
const wrap = ref<HTMLElement | null>(null)
const tip = ref<Record<string, string> | null>(null)

const blockedReason = computed(() => {
  if (!availability.value) return 'Verificando o Linkus…'
  if (!availability.value.available) return availability.value.reason ?? 'Ligação indisponível.'
  if (!props.phone) return 'Este contato não tem telefone.'
  return null
})
const label = computed(() => state.value === 'calling' ? 'Chamando…' : state.value === 'done' ? 'Atenda o Linkus' : 'Ligar')
const tipText = computed(() => blockedReason.value
  ?? (phone.state === 'ready' ? `Ligar por aqui, pelo navegador (ramal ${availability.value?.extension})`
  : state.value === 'done' ? 'O PABX está tocando o seu Linkus — atenda pra completar a ligação.' : `Ligar pelo Linkus (ramal ${availability.value?.extension})`))
const ariaLabel = computed(() => blockedReason.value ? `Ligar indisponível: ${blockedReason.value}` : `Ligar para ${props.contactName || 'o contato'} pelo Linkus`)

function showTip() {
  const el = wrap.value
  if (!el) return
  const r = el.getBoundingClientRect()
  const below = r.top < 80
  tip.value = {
    left: `${Math.min(window.innerWidth - 12, Math.max(12, r.left + r.width / 2))}px`,
    top: `${below ? r.bottom + 8 : r.top - 8}px`,
    transform: below ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
  }
}

async function onClick() {
  if (blockedReason.value || state.value === 'calling') return
  state.value = 'calling'
  if (phone.state === 'ready') {
    const resolved = await resolveCallNumber(token.value, props.conversationId)
    try {
      if (!resolved.ok || !resolved.number) throw new Error(resolved.problem ?? 'O contato não tem um telefone válido.')
      await placeCall(resolved.number, { name: props.contactName || resolved.contactName || undefined, conversationId: props.conversationId })
    } catch (e) {
      Swal.fire({ icon: 'warning', title: 'Não deu pra ligar', text: (e as Error).message })
    } finally {
      state.value = 'idle'
    }
    return
  }
  const result = await startLinkusCall(token.value, props.conversationId)
  if (!result.ok) {
    state.value = 'idle'
    Swal.fire({ icon: 'warning', title: 'Não deu pra ligar', text: result.problem ?? 'O PABX não aceitou a ligação.' })
    return
  }
  state.value = 'done'
  Swal.fire({
    toast: true, position: 'bottom-end', timer: 5000, showConfirmButton: false, icon: 'success',
    title: `Atenda o Linkus (ramal ${result.extension})`, text: `Em seguida o PABX liga para ${props.contactName || result.number}.`,
  })
  setTimeout(() => { if (state.value === 'done') state.value = 'idle' }, 6000)
}

onMounted(() => { void loadLinkus(token.value) })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("span", {
      ref_key: "wrap",
      ref: wrap,
      class: _normalizeClass(["lcb", [`lcb--${__props.variant}`, { 'lcb--off': blockedReason.value, 'lcb--busy': state.value === 'calling', 'lcb--done': state.value === 'done' }]]),
      onMouseenter: showTip,
      onMouseleave: _cache[0] || (_cache[0] = ($event: any) => (tip.value = null)),
      onFocusin: showTip,
      onFocusout: _cache[1] || (_cache[1] = ($event: any) => (tip.value = null))
    }, [
      _createElementVNode("button", {
        type: "button",
        class: "lcb-btn",
        "aria-disabled": !!blockedReason.value || state.value === 'calling',
        "aria-label": ariaLabel.value,
        onClick: _withModifiers(onClick, ["stop"])
      }, [
        (state.value === 'calling')
          ? (_openBlock(), _createElementBlock("span", _hoisted_2))
          : (state.value === 'done')
            ? (_openBlock(), _createElementBlock("svg", _hoisted_3, [...(_cache[2] || (_cache[2] = [
                _createElementVNode("polyline", { points: "20 6 9 17 4 12" }, null, -1)
              ]))]))
            : (_openBlock(), _createElementBlock("svg", _hoisted_4, [...(_cache[3] || (_cache[3] = [
                _createElementVNode("path", { d: "M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24 11.4 11.4 0 0 0 3.57.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1.02l-2.2 2.2z" }, null, -1)
              ]))])),
        (__props.variant !== 'row')
          ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(label.value), 1))
          : _createCommentVNode("", true)
      ], 8, _hoisted_1)
    ], 34),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (tip.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: _normalizeClass(["lcb-tip", { 'lcb-tip--warn': !!blockedReason.value }]),
            style: _normalizeStyle(tip.value),
            role: "tooltip"
          }, _toDisplayString(tipText.value), 7))
        : _createCommentVNode("", true)
    ]))
  ], 64))
}
}

})